import {
  ClientMsg,
  ServerToClientMsg,
  backoffMs,
  encode,
  safeParse,
  type AppList,
  type AppWindows,
  type CodexUsage,
  type FsEntry,
  type LinkState,
  type MacMetrics,
  type PowerMode,
  type Proc,
  type Project,
  type Session,
  type WebView,
} from '@mac-remote/protocol';
import { getTelegramWebApp } from './telegram';

const TOKEN_KEY = 'mac-remote:session';

/** How the app itself is doing — distinct from how the *Mac* is doing (LinkState). */
export type ConnState =
  | { kind: 'connecting' }
  | { kind: 'ready' }
  | { kind: 'reconnecting'; attempt: number }
  /** Terminal: needs the user to act. `reopen` means initData went stale. */
  | { kind: 'failed'; reason: string; reopen: boolean };

export interface ConnectionEvents {
  conn(state: ConnState): void;
  link(state: LinkState, latencyMs: number | null, lastSeenAt: number | null): void;
  projects(projects: Project[]): void;
  sessions(sessions: Session[]): void;
  snapshot(sid: string, data: Uint8Array): void;
  busy(sid: string, busy: boolean): void;
  data(sid: string, data: Uint8Array): void;
  exit(sid: string, code: number | null): void;
  error(msg: string): void;
  power(mode: PowerMode): void;
  /** null while the Mac is asleep or has not answered — the screen says so rather
   *  than leaving yesterday's battery level on display. */
  metrics(metrics: MacMetrics | null): void;
  /** Codex's limits, or null while the Mac is unreachable. A reading that failed
   *  arrives as `{ ok: false }` and carries the reason — that is not the same thing. */
  codexUsage(usage: CodexUsage | null): void;
  apps(apps: AppList): void;
  appWindows(windows: AppWindows): void;
  /** A screen frame, kept as base64 (not decoded) so it drops straight into an
   *  `<img src="data:image/jpeg;base64,…">`. `at` is the Mac's capture time. */
  screen(b64: string, w: number, h: number, at: number): void;
  clip(text: string): void;
  fs(path: string, entries: FsEntry[]): void;
  /** A downloaded file's bytes, base64. The app turns it into a browser download. */
  file(name: string, b64: string): void;
  /** The file reached the Telegram chat. Named, so the app can say which one. */
  fileSent(name: string): void;
  ps(procs: Proc[]): void;
  webcam(b64: string, w: number, h: number, at: number): void;
  /** What the Mac's headless browser shows after an action, with the agent's verdict. */
  web(view: WebView, at: number): void;
}

type Listener<K extends keyof ConnectionEvents> = ConnectionEvents[K];

async function post<T>(path: string, body: unknown, token?: string): Promise<T> {
  const res = await fetch(path, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      ...(token ? { authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body ?? {}),
  });
  if (!res.ok) {
    const err = new Error(`${path} → ${res.status}`);
    (err as Error & { status?: number }).status = res.status;
    throw err;
  }
  return (await res.json()) as T;
}

export class Connection {
  #ws: WebSocket | null = null;
  #attempt = 0;
  #closed = false;
  #listeners = new Map<keyof ConnectionEvents, Set<(...args: never[]) => void>>();
  #retryTimer: number | null = null;

  on<K extends keyof ConnectionEvents>(event: K, fn: Listener<K>): () => void {
    let set = this.#listeners.get(event);
    if (!set) {
      set = new Set();
      this.#listeners.set(event, set);
    }
    set.add(fn as (...args: never[]) => void);
    return () => set!.delete(fn as (...args: never[]) => void);
  }

  #emit<K extends keyof ConnectionEvents>(event: K, ...args: Parameters<ConnectionEvents[K]>): void {
    for (const fn of this.#listeners.get(event) ?? []) (fn as (...a: unknown[]) => void)(...args);
  }

  /**
   * Trade initData for a session token, but only when we have to.
   *
   * Telegram stamps `auth_date` when the app launches and never refreshes it, so
   * initData is only good for the first few minutes. The stored token is what makes
   * a reconnect an hour later work at all — falling back to initData then would be
   * rejected as stale, which is not an error the user can do anything about except
   * reopen the app. Hence `reopen: true` on that specific failure.
   */
  async #session(): Promise<string> {
    const stored = localStorage.getItem(TOKEN_KEY);
    if (stored) return stored;

    const wa = getTelegramWebApp();
    const initData = wa?.initData ?? '';
    if (!initData) throw Object.assign(new Error('нет initData'), { reopen: true });

    const { token } = await post<{ token: string }>('/api/auth', { initData });
    localStorage.setItem(TOKEN_KEY, token);
    return token;
  }

  async #ticket(): Promise<string> {
    let token = await this.#session();
    try {
      const { ticket } = await post<{ ticket: string }>('/api/ws-ticket', {}, token);
      return ticket;
    } catch (err) {
      // Session rejected (expired, or the bot token was rotated). Drop it and try
      // once with initData — which only works if the app opened recently.
      if ((err as { status?: number }).status !== 401) throw err;
      localStorage.removeItem(TOKEN_KEY);
      token = await this.#session();
      const { ticket } = await post<{ ticket: string }>('/api/ws-ticket', {}, token);
      return ticket;
    }
  }

  async connect(): Promise<void> {
    if (this.#closed) return;
    this.#emit('conn', this.#attempt === 0 ? { kind: 'connecting' } : { kind: 'reconnecting', attempt: this.#attempt });

    let ticket: string;
    try {
      ticket = await this.#ticket();
    } catch (err) {
      const e = err as Error & { status?: number; reopen?: boolean };
      const stale = e.reopen || e.status === 401;
      if (stale) {
        this.#emit('conn', {
          kind: 'failed',
          reason: 'Сессия истекла',
          reopen: true,
        });
        return;
      }
      this.#scheduleRetry();
      return;
    }

    const proto = location.protocol === 'https:' ? 'wss' : 'ws';
    const ws = new WebSocket(`${proto}://${location.host}/ws/client?ticket=${ticket}`);
    this.#ws = ws;

    ws.onopen = () => {
      this.#attempt = 0;
      this.#emit('conn', { kind: 'ready' });
    };

    ws.onmessage = (ev) => {
      const parsed = safeParse(ServerToClientMsg, String(ev.data));
      if (!parsed.ok) return;
      this.#dispatch(parsed.msg);
    };

    ws.onclose = () => {
      this.#ws = null;
      if (this.#closed) return;
      this.#scheduleRetry();
    };

    ws.onerror = () => {
      /* onclose always follows; retry is scheduled there. */
    };
  }

  #scheduleRetry(): void {
    const delay = backoffMs(this.#attempt++);
    this.#emit('conn', { kind: 'reconnecting', attempt: this.#attempt });
    if (this.#retryTimer) clearTimeout(this.#retryTimer);
    this.#retryTimer = window.setTimeout(() => void this.connect(), delay);
  }

  #dispatch(msg: ServerToClientMsg): void {
    const b = (b64: string) => Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
    switch (msg.t) {
      case 's.link':
        this.#emit('link', msg.state, msg.latencyMs, msg.lastSeenAt);
        return;
      case 's.projects':
        this.#emit('projects', msg.projects);
        return;
      case 's.sessions':
        this.#emit('sessions', msg.sessions);
        return;
      case 's.snapshot':
        this.#emit('snapshot', msg.sid, b(msg.b64));
        return;
      case 's.busy':
        this.#emit('busy', msg.sid, msg.busy);
        return;
      case 's.data':
        this.#emit('data', msg.sid, b(msg.b64));
        return;
      case 's.exit':
        this.#emit('exit', msg.sid, msg.code);
        return;
      case 's.err':
        this.#emit('error', msg.msg);
        return;
      case 's.power':
        this.#emit('power', msg.mode);
        return;
      case 's.metrics':
        this.#emit('metrics', msg.metrics);
        return;
      case 's.codex_usage':
        this.#emit('codexUsage', msg.usage);
        return;
      case 's.apps':
        this.#emit('apps', msg.apps);
        return;
      case 's.app_windows':
        this.#emit('appWindows', msg.windows);
        return;
      case 's.screen':
        this.#emit('screen', msg.b64, msg.w, msg.h, msg.at);
        return;
      case 's.clip':
        this.#emit('clip', msg.text);
        return;
      case 's.fs':
        this.#emit('fs', msg.path, msg.entries);
        return;
      case 's.file':
        this.#emit('file', msg.name, msg.b64);
        return;
      case 's.file_sent':
        this.#emit('fileSent', msg.name);
        return;
      case 's.ps':
        this.#emit('ps', msg.procs);
        return;
      case 's.webcam':
        this.#emit('webcam', msg.b64, msg.w, msg.h, msg.at);
        return;
      case 's.web':
        this.#emit('web', msg.view, msg.at);
        return;
      case 's.pong':
        return;
    }
  }

  send(msg: ClientMsg): boolean {
    if (this.#ws?.readyState !== WebSocket.OPEN) return false;
    this.#ws.send(encode(msg));
    return true;
  }

  close(): void {
    this.#closed = true;
    if (this.#retryTimer) clearTimeout(this.#retryTimer);
    this.#ws?.close();
  }
}
